import dayjs from 'dayjs';
import { NewContractOrg } from '../types/Application/newContract';
import {
  ApplicationFile,
  ApplicationOrg,
  EmploymentByMonth,
  EmploymentCertification,
  OtherCertification,
  WageSlip,
  WorkedMonth,
} from '../types/Application/application';
import { CertificateType, TypeOfFile } from '../constants/Enum';
import { getAsBase64 } from './FileUtils';
import { CsnApplicationOrg } from '../types/Application/csnApplication';
import {
  EmployerInfo,
  EmploymentPeriodView,
  MonthData,
  NonQualifyingPeriodText,
  NonQualifyingPeriodView,
  Worksite,
} from '../types/ApplicationV3/application';
import { GroupedOption } from '../components/formComponents/Base/Dropdown';
import { BisnodeWorksite } from '../types/Bisnode/BisnodeWorksite';
import { PremiumPaymentThresholdContainer } from '../types/Config';

export const getMonthName = (month: number): string => {
  const months = [
    'januari',
    'februari',
    'mars',
    'april',
    'maj',
    'juni',
    'juli',
    'augusti',
    'september',
    'oktober',
    'november',
    'december',
  ];

  return month >= 1 && month <= 12 ? months[month - 1] : 'Ogiltig månad';
};

export function generateMonthsArray(employedFrom: string, employedTo: string) {
  const startDate = new Date(employedFrom);
  const endDate = new Date(employedTo);
  const currentDate = new Date();

  // Calculate the date 24 months ago from the current date
  const twentyFourMonthsAgo = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() - 23,
    currentDate.getDate()
  );

  // If the employment started after the last 24 months, adjust the start date
  if (startDate < twentyFourMonthsAgo) {
    startDate.setTime(twentyFourMonthsAgo.getTime());
  }

  const months = [];

  while (startDate <= endDate && startDate <= currentDate) {
    const year = startDate.getFullYear();
    const month = startDate.getMonth() + 1; // Month is 0-indexed, so add 1
    const monthString = `${getMonthName(month)} ${year}`;

    months.push({ name: monthString, month: month, year: year });
    startDate.setMonth(startDate.getMonth() + 1); // Move to the next month
  }

  return months;
}

export function getDaysInMonth(year: number, month: number) {
  if (isNaN(month)) return 31;
  return dayjs(`${year}-${month}`).daysInMonth();
}

export function getRequiredWorkingHours(daysInMonth: number) {
  if (!Number.isInteger(daysInMonth)) {
    daysInMonth = 31;
  }

  return Math.round((daysInMonth / 7) * 16);
}

export const createFileObject = async (
  file: File,
  prefix: string,
  type: TypeOfFile,
  withoutPrefix?: boolean
) => {
  const base64String = await getAsBase64(file);
  const applicationFile: ApplicationFile = {
    id: Date.now().toString(),
    name: withoutPrefix ? file.name : `${prefix} - ${file.name}`,
    filename: file.name,
    mimeType: file.type,
    file: base64String,
    type: type,
  };

  return applicationFile;
};

const updateWageSlips = (
  wageSlips: WageSlip[],
  files: ApplicationFile[],
  comment: string | undefined,
  hoursWorked: number | undefined,
  workedMonth: number,
  workedYear: number
): WageSlip[] => {
  return wageSlips.map((wageSlip) => {
    if (wageSlip.month === workedMonth && wageSlip.year === workedYear) {
      return {
        hoursWorked: hoursWorked,
        files: [...wageSlip.files, ...files],
        month: wageSlip.month,
        year: wageSlip.year,
        comment: comment ? comment : wageSlip.comment,
      };
    }
    return wageSlip;
  }) as WageSlip[];
};

export const handleWageSlipUpdate = async (
  editedEmployment: NewContractOrg | ApplicationOrg | CsnApplicationOrg,
  files: FileList | File[],
  documentComment: string | undefined,
  hoursWorked: number | undefined,
  workedMonth: number,
  workedYear: number,
  employments: NewContractOrg[] | ApplicationOrg[] | CsnApplicationOrg[]
) => {
  const newFilesPromise: Promise<ApplicationFile>[] = Array.from(files).map(
    (file) =>
      createFileObject(
        file,
        `Lönespec ${getMonthName(workedMonth)} ${workedYear}`,
        TypeOfFile.WageSlip
      )
  );

  const newFiles: ApplicationFile[] = await Promise.all(newFilesPromise);
  if (!editedEmployment.wageSlip) {
    const isDuplicate = checkIfFileExist(
      newFiles,
      editedEmployment,
      employments
    );
    if (isDuplicate) return editedEmployment;

    const newWageSlip = {
      hoursWorked: hoursWorked,
      files: newFiles,
      month: workedMonth,
      year: workedYear,
      comment: documentComment,
    } as WageSlip;

    return {
      ...editedEmployment,
      wageSlip: [newWageSlip],
    };
  } else {
    const isExistingWageSlip = editedEmployment.wageSlip.some(
      (wageSlip) =>
        wageSlip.month === workedMonth && wageSlip.year === workedYear
    );
    if (isExistingWageSlip) {
      const updatedWageSlips = updateWageSlips(
        editedEmployment.wageSlip,
        newFiles,
        documentComment,
        hoursWorked,
        workedMonth,
        workedYear
      );
      return {
        ...editedEmployment,
        wageSlip: updatedWageSlips,
      };
    } else {
      const newWageSlip = {
        hoursWorked: hoursWorked,
        files: newFiles,
        month: workedMonth,
        year: workedYear,
        comment: documentComment,
      } as WageSlip;
      return {
        ...editedEmployment,
        wageSlip: [...editedEmployment.wageSlip, newWageSlip],
      };
    }
  }
};

export const handleEmploymentCertUpdate = async (
  editedEmployment: NewContractOrg | ApplicationOrg,
  files: FileList | File[],
  documentComment: string | undefined,
  employments: NewContractOrg[] | ApplicationOrg[],
  withoutPrefix?: boolean,
  typeOfFile?: TypeOfFile
) => {
  const newFilesPromise: Promise<ApplicationFile>[] = Array.from(files).map(
    (file) =>
      createFileObject(
        file,
        'Arbetsgivarintyg',
        typeOfFile || TypeOfFile.EmploymentCertificate,
        withoutPrefix
      )
  );

  const newFiles: ApplicationFile[] = await Promise.all(newFilesPromise);

  const isDuplicate = checkIfFileExist(newFiles, editedEmployment, employments);
  if (isDuplicate) return editedEmployment;

  if (editedEmployment.employmentCert) {
    return {
      ...editedEmployment,
      employmentCert: {
        comment: documentComment
          ? documentComment
          : editedEmployment.employmentCert.comment,
        files: [...editedEmployment.employmentCert.files, ...newFiles],
      },
    };
  } else {
    const newEmploymentCert = {
      files: newFiles,
      comment: documentComment,
    } as EmploymentCertification;
    return {
      ...editedEmployment,
      employmentCert: newEmploymentCert,
    };
  }
};

const checkIfFileExist = (
  newFiles: ApplicationFile[],
  editedEmployment: NewContractOrg | ApplicationOrg,
  employments: NewContractOrg[] | ApplicationOrg[]
) => {
  if (!editedEmployment) return;
  let isDuplicate = false;
  const employmentsArr = Object.values(employments);
  for (const newFile of newFiles) {
    if (editedEmployment.wageSlip !== undefined) {
      isDuplicate = editedEmployment.wageSlip.some((wageSlip: WageSlip) => {
        return wageSlip.files.some(
          (wageSlipFile: ApplicationFile) =>
            wageSlipFile.filename === newFile.filename
        );
      });
    }
    if (editedEmployment.employmentCert !== undefined) {
      isDuplicate = editedEmployment.employmentCert.files.some(
        (employmentCertFile: ApplicationFile) =>
          employmentCertFile.filename === newFile.filename
      );
    }
    if (isDuplicate) {
      break;
    }
    for (const employment of employmentsArr) {
      if (employment.wageSlip !== undefined) {
        isDuplicate = employment.wageSlip.some((wageSlip: WageSlip) => {
          return wageSlip.files.some(
            (wageSlipFile: ApplicationFile) =>
              wageSlipFile.filename === newFile.filename
          );
        });
      } else if (employment.employmentCert !== undefined) {
        isDuplicate = employment.employmentCert.files.some(
          (employmentCertFile: ApplicationFile) =>
            employmentCertFile.filename === newFile.filename
        );
      }

      if (isDuplicate) {
        break;
      }
    }
  }
  return isDuplicate;
};

export const removeFileFromWageSlip = (
  editedEmployment: NewContractOrg | ApplicationOrg | CsnApplicationOrg,
  fileName: string
) => {
  if (!editedEmployment.wageSlip) return editedEmployment;
  let updatedState;

  updatedState = {
    ...editedEmployment,
    wageSlip: editedEmployment.wageSlip.map((wageslip: WageSlip) => ({
      ...wageslip,
      files: wageslip.files.filter(
        (file: ApplicationFile) => file.name !== fileName
      ),
    })),
  };

  const allFilesRemoved = updatedState.wageSlip!.every(
    (wageSlip: WageSlip) => wageSlip.files.length === 0
  );

  if (allFilesRemoved) {
    return { ...editedEmployment, wageSlip: undefined };
  } else {
    let filteredUpdatedState;
    filteredUpdatedState = {
      ...updatedState,
      wageSlip: updatedState.wageSlip!.filter(
        (wageSlip: WageSlip) => wageSlip.files.length > 0
      ),
    };

    return filteredUpdatedState;
  }
};

export const removeFileFromEmploymentCert = (
  editedEmployment: NewContractOrg | ApplicationOrg,
  fileName: string
) => {
  if (!editedEmployment.employmentCert) return editedEmployment;

  const filteredFiles = editedEmployment.employmentCert.files.filter(
    (file: ApplicationFile) => file.name !== fileName
  );

  if (filteredFiles.length === 0) {
    return { ...editedEmployment, employmentCert: undefined };
  } else {
    return {
      ...editedEmployment,
      employmentCert: {
        ...editedEmployment.employmentCert,
        files: filteredFiles,
      },
    };
  }
};

export const uploadOtherCertificate = async (
  editedEmployment: NewContractOrg | ApplicationOrg,
  title: string,
  comment: string,
  files: FileList
) => {
  const newCertificate = {
    title,
    comment,
    files: [] as ApplicationFile[],
  } as OtherCertification;

  const newFilesPromise: Promise<ApplicationFile>[] = Array.from(files).map(
    (file: File) => {
      return createFileObject(
        file,
        newCertificate.title,
        TypeOfFile.OtherCertificate
      );
    }
  );

  const newFiles = await Promise.all(newFilesPromise);

  newFiles.forEach((file) => {
    newCertificate.files.push(file);
  });

  return {
    ...editedEmployment,
    otherCertificates: editedEmployment.otherCertificates
      ? [...editedEmployment.otherCertificates, newCertificate]
      : [newCertificate],
  };
};

export const removeOtherCertificateFile = (
  editedEmployment: NewContractOrg | ApplicationOrg | CsnApplicationOrg,
  fileName: string
) => {
  if (!editedEmployment.otherCertificates) return editedEmployment;
  let updatedState;
  updatedState = {
    ...editedEmployment,
    otherCertificates: editedEmployment.otherCertificates.map(
      (otherCertificate: OtherCertification) => ({
        ...otherCertificate,
        files: otherCertificate.files.filter(
          (file: ApplicationFile) => file.name !== fileName
        ),
      })
    ),
  };

  const allFilesRemoved = updatedState.otherCertificates!.every(
    (otherCertificate: OtherCertification) =>
      otherCertificate.files.length === 0
  );

  if (allFilesRemoved) {
    return {
      ...editedEmployment,
      otherCertificates: undefined,
    };
  } else {
    let filteredUpdatedState;
    filteredUpdatedState = {
      ...updatedState,
      otherCertificates: updatedState.otherCertificates!.filter(
        (otherCertificate: OtherCertification) =>
          otherCertificate.files.length > 0
      ),
    } as ApplicationOrg;

    return filteredUpdatedState;
  }
};

export const getWorkedMonthsEmploymentCert = (
  employedFrom: string,
  employedTo: string
) => {
  const workedMonthsArr: WorkedMonth[] = [];
  const monthsArray = generateMonthsArray(employedFrom, employedTo);
  monthsArray.forEach((empMonthYear) => {
    const workedMonth: WorkedMonth = {
      minimumExpectedHours: undefined,
      claimedHoursWorked: undefined,
      certificateType: CertificateType.EmploymentCertification,
      name: getMonthName(empMonthYear.month),
      number: empMonthYear.month,
      year: empMonthYear.year,
    };

    workedMonthsArr.push(workedMonth);
  });

  return workedMonthsArr;
};

export const getWorkedMonthsWageSlips = (
  wageSlips: WageSlip[],
  employedFrom: string,
  employedTo: string
) => {
  const workedMonthsArr: WorkedMonth[] = [];
  const monthsArray = generateMonthsArray(employedFrom, employedTo);

  monthsArray.forEach((empMonthYear) => {
    const correspondingWageSlips = wageSlips.filter((wageSlip) => {
      return (
        wageSlip.year === empMonthYear.year &&
        wageSlip.month === empMonthYear.month
      );
    });

    const claimedHoursWorked = correspondingWageSlips.reduce(
      (totalHours, wageSlip) => {
        return totalHours + wageSlip.hoursWorked;
      },
      0
    );

    const workedMonth: WorkedMonth = {
      minimumExpectedHours: getRequiredWorkingHours(
        getDaysInMonth(empMonthYear.year, empMonthYear.month)
      ),
      claimedHoursWorked: claimedHoursWorked,
      certificateType: CertificateType.WageSlip,
      name: getMonthName(empMonthYear.month),
      number: empMonthYear.month,
      year: empMonthYear.year,
    };

    workedMonthsArr.push(workedMonth);
  });

  return workedMonthsArr;
};

export const getEmploymentsByMonth = (
  employments: ApplicationOrg[]
): EmploymentByMonth[] => {
  const workedMonthsArr: EmploymentByMonth[] = [];
  let prevDate: Date | undefined;

  for (let i = 0; i > -24; i--) {
    let currentDate: undefined | Date = undefined;
    if (prevDate === undefined) {
      currentDate = new Date();
      //setMonth() might return the same month or skip a month.
      //We instead use the 15th for rendering the calendar view since the date of month does not matter here.
      currentDate.setDate(15);
    } else {
      currentDate = prevDate;
      currentDate.setMonth(prevDate.getMonth() - 1);
    }
    let currentMonth = currentDate.getMonth();
    let currentYear = currentDate.getFullYear();
    workedMonthsArr.push({
      id: `${currentMonth}-${currentYear}-${Date.now().toString()}`,
      month: currentMonth + 1,
      monthText: `${getMonthName(currentMonth + 1)} ${currentYear}`,
      year: currentYear,
      expectedHours: getRequiredWorkingHours(
        getDaysInMonth(currentYear, currentMonth + 1)
      ),
      totalHoursWorked: undefined,
      monthHasEmploymentCert: false,
      isEmptyMonth: true,
    });
    prevDate = currentDate;
  }

  employments.forEach((employment) => {
    if (employment.wageSlip !== undefined) {
      employment.wageSlip.forEach((wageSlip) => {
        const existingMonthYear = workedMonthsArr.find((monthYear) => {
          return (
            monthYear.year === wageSlip.year &&
            monthYear.month === wageSlip.month
          );
        });
        if (existingMonthYear !== undefined) {
          existingMonthYear.id = `${employment.nodeId}-${wageSlip.month}-${wageSlip.year}`;
          existingMonthYear.monthHasEmploymentCert = false;

          if (existingMonthYear.totalHoursWorked) {
            existingMonthYear.totalHoursWorked += wageSlip.hoursWorked;
          } else {
            existingMonthYear.totalHoursWorked = wageSlip.hoursWorked;
          }
          existingMonthYear.isEmptyMonth = false;
          if (existingMonthYear.employments !== undefined) {
            existingMonthYear.employments.push({
              worksiteName: employment.parentCompany.orgName,
              nodeId: employment.nodeId,
              hoursWorked: wageSlip.hoursWorked ?? undefined,
            });
          } else {
            existingMonthYear.employments = [
              {
                worksiteName: employment.parentCompany.orgName,
                nodeId: employment.nodeId,
                hoursWorked: wageSlip.hoursWorked ?? undefined,
              },
            ];
          }
        }
      });
    } else if (employment.employmentCert !== undefined) {
      if (!employment.employedFrom || !employment.employedTo) return;
      const monthsArray = generateMonthsArray(
        employment.employedFrom,
        employment.employedTo
      );
      monthsArray.forEach((empMonthYear) => {
        var existingMonthYear = workedMonthsArr.find((monthYear) => {
          return (
            monthYear.year === empMonthYear.year &&
            monthYear.month === empMonthYear.month
          );
        });
        if (existingMonthYear !== undefined) {
          existingMonthYear.id = `${employment.nodeId}-${empMonthYear.month}-${empMonthYear.year}`;
          existingMonthYear.monthHasEmploymentCert = true;
          existingMonthYear.totalHoursWorked =
            existingMonthYear.totalHoursWorked ?? 0;
          existingMonthYear.isEmptyMonth = false;
          if (existingMonthYear.employments !== undefined) {
            existingMonthYear.employments.push({
              worksiteName: employment.parentCompany.orgName,
              nodeId: employment.nodeId,
              hoursWorked: undefined,
            });
          } else {
            existingMonthYear.employments = [
              {
                worksiteName: employment.parentCompany.orgName,
                nodeId: employment.nodeId,
                hoursWorked: undefined,
              },
            ];
          }
        }
      });
    }
  });
  return workedMonthsArr;
};

export const addCenturyToPersonalNumber = (personalNumber: string): string => {
  const year = parseInt(personalNumber.substring(0, 2), 10);
  const currentYear = new Date().getFullYear() % 100;
  const century =
    year <= currentYear
      ? Math.floor(new Date().getFullYear() / 100)
      : Math.floor(new Date().getFullYear() / 100) - 1;

  return `${century}${personalNumber}`;
};

const monthNamesShort = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'Maj',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Okt',
  'Nov',
  'Dec',
];

/**
 * Reference date for worked-time calculations.
 * We anchor on the current month only when payroll exists for it.
 * Using day 15 to avoid month-boundary issues.
 */
const getWorkedTimeReferenceDate = (
  includeCurrentMonth: boolean,
  asOfDate: Date = new Date()
): Date => {
  const d = new Date(asOfDate);
  if (!includeCurrentMonth) {
    d.setMonth(d.getMonth() - 1);
  }
  d.setDate(15); // avoid boundary issues
  return d;
};

const hasCurrentMonthPayroll = (
  employments: EmploymentPeriodView[],
  asOfDate: Date = new Date()
): boolean => {
  const currentYear = asOfDate.getFullYear();
  const currentMonth = asOfDate.getMonth() + 1;

  return employments.some((employment) =>
    (employment.workedMonths ?? []).some(
      (workedMonth) =>
        workedMonth.year === currentYear &&
        workedMonth.month === currentMonth &&
        workedMonth.hasPayroll
    )
  );
};

/**
 * Calculate how many months back we need to look based on NonQualifyingPeriods.
 * Logic:
 * - Base period is 24 months
 * - ÖHT (överhoppad tid) must be consecutive to count
 * - At least one month of ÖHT must be within the original 24-month period
 * - If ÖHT extends beyond the 24-month period, add ONE extra month after the last skipped month
 *
 * Examples:
 * - Sick month 20, 21, 22 → extends with month 25, 26, 27
 * - Sick month 24, 25, 26 → extends with month 25, 26, 27 (only 1 month beyond skipped)
 * - Sick month 24 and 26 (not consecutive) → month 26 doesn't count, no extension
 */
const calculateMonthsBack = (
  employments: EmploymentPeriodView[],
  nonQualifyingPeriods: NonQualifyingPeriodView[],
  asOfDate?: Date
): number => {
  const referenceDate = getWorkedTimeReferenceDate(
    hasCurrentMonthPayroll(employments, asOfDate),
    asOfDate
  );

  if (nonQualifyingPeriods.length === 0) {
    return 24;
  }

  // Find all consecutive groups of non-qualifying months
  const nonQualifyingMonths = new Set<string>();

  for (const period of nonQualifyingPeriods) {
    const startDate = new Date(period.startDate);
    const endDate = period.isOngoing ? new Date() : new Date(period.endDate);

    let currentPeriodDate = new Date(startDate);
    currentPeriodDate.setDate(15);

    while (currentPeriodDate <= endDate) {
      const year = currentPeriodDate.getFullYear();
      const month = currentPeriodDate.getMonth() + 1;
      const key = `${year}-${String(month).padStart(2, '0')}`;
      nonQualifyingMonths.add(key);
      currentPeriodDate.setMonth(currentPeriodDate.getMonth() + 1);
    }
  }

  let maxExtension = 0;

  // Check each month from 0 to 23 (the original 24-month period)
  for (let i = 0; i < 24; i++) {
    const date = new Date(referenceDate);
    date.setMonth(referenceDate.getMonth() - i);
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const key = `${year}-${String(month).padStart(2, '0')}`;

    if (nonQualifyingMonths.has(key)) {
      // Found a non-qualifying month within the base period
      // Now find the consecutive group it belongs to
      let consecutiveCount = 0;
      let checkIndex = i;

      // Count backwards to find start of consecutive group
      while (checkIndex < 100) {
        const checkDate = new Date(referenceDate);
        checkDate.setMonth(referenceDate.getMonth() - checkIndex);
        const checkYear = checkDate.getFullYear();
        const checkMonth = checkDate.getMonth() + 1;
        const checkKey = `${checkYear}-${String(checkMonth).padStart(2, '0')}`;

        if (nonQualifyingMonths.has(checkKey)) {
          consecutiveCount++;
          checkIndex++;
        } else {
          break;
        }
      }

      // Calculate extension: number of consecutive months + 1
      const extension = consecutiveCount;

      if (extension > maxExtension) {
        maxExtension = extension;
      }
    }
  }

  return 24 + maxExtension;
};

export const buildMonthData = (
  employments: EmploymentPeriodView[],
  nonQualifyingPeriods: NonQualifyingPeriodView[] = [],
  asOfDate?: Date,
  premiumPaymentThreshold?: PremiumPaymentThresholdContainer
): MonthData[] => {
  const monthMap = new Map<string, MonthData>();
  const includeCurrentMonth = hasCurrentMonthPayroll(employments, asOfDate);

  const monthsBack = calculateMonthsBack(
    employments,
    nonQualifyingPeriods,
    asOfDate
  );

  const referenceDate = getWorkedTimeReferenceDate(
    includeCurrentMonth,
    asOfDate
  );

  for (let i = 0; i < monthsBack; i++) {
    const date = new Date(referenceDate);
    date.setMonth(referenceDate.getMonth() - i);

    const year = date.getFullYear();
    const month = date.getMonth() + 1; // 1-indexed
    const key = `${year}-${String(month).padStart(2, '0')}`;

    monthMap.set(key, {
      id: key,
      monthLabel: monthNamesShort[month - 1],
      year: year,
      employers: [],
      month: month,
    });
  }

  for (const emp of employments) {
    for (const wm of emp?.workedMonths ?? []) {
      const key = `${wm.year}-${String(wm.month).padStart(2, '0')}`;

      if (monthMap.has(key)) {
        const employer: EmployerInfo = {
          id: emp.id,
          name: emp.organizationName,
          meetsSalaryRequirement: wm.meetsSalaryRequirement,
          meetsSalaryRequirementWithAnotherEmployment:
            wm.meetsSalaryRequirementWithAnotherEmployment,
          attachments: wm.attachments ? wm.attachments : [],
          isNonQualifyingPeriod: false,
          hasAttachment: wm.attachments ? wm.attachments.length > 0 : false,
          hasAttachmentFromPreviousApplication:
            wm.hasAttachmentFromPreviousApplication ?? false,
          hasWorkedTimeEmploymentCertificate:
            (emp.workedTimeEmploymentCertificates?.length ?? 0) > 0,
          retrievedFromFora: emp.retrievedFromFora,
          period: {
            start: emp.startDate,
            end: emp.endDate,
          },
        };

        monthMap.get(key)!.employers.push(employer);
      }
    }
  }

  for (const period of nonQualifyingPeriods) {
    const startDate = new Date(period.startDate);
    const endDate = period.isOngoing ? new Date() : new Date(period.endDate);

    let currentPeriodDate = new Date(startDate);
    currentPeriodDate.setDate(15); // Use 15th to avoid boundary issues

    while (currentPeriodDate <= endDate) {
      const year = currentPeriodDate.getFullYear();
      const month = currentPeriodDate.getMonth() + 1;
      const key = `${year}-${String(month).padStart(2, '0')}`;

      if (monthMap.has(key)) {
        const nonQualifyingEmployer: EmployerInfo = {
          id: period.id,
          name: NonQualifyingPeriodText[period.nonQualifyingPeriodType],
          attachments: period.attachments || [],
          isNonQualifyingPeriod: true,
          hasAttachment: true,
          hasAttachmentFromPreviousApplication:
            period.hasAttachmentFromPreviousApplication,
          nonQualifyingPeriodType: period.nonQualifyingPeriodType,
          period: {
            start: period.startDate,
            end: period.isOngoing ? '' : period.endDate,
          },
        };

        monthMap.get(key)!.employers.push(nonQualifyingEmployer);
      }

      currentPeriodDate.setMonth(currentPeriodDate.getMonth() + 1);
    }
  }

  // Compute meetsSalaryRequirementWithAnotherEmployment:
  // For each month, if no single employer meets the salary requirement individually,
  // check if the combined totalPremiumBasedPayAmount across all employers meets the threshold.
  if (premiumPaymentThreshold !== undefined) {
    // Build a map of monthKey -> employerId -> totalPremiumBasedPayAmount
    const amountMap = new Map<string, Map<string, number>>();
    for (const emp of employments) {
      for (const wm of emp?.workedMonths ?? []) {
        const key = `${wm.year}-${String(wm.month).padStart(2, '0')}`;
        if (!monthMap.has(key)) continue;
        if (!amountMap.has(key)) amountMap.set(key, new Map());
        amountMap.get(key)!.set(emp.id, wm.totalPremiumBasedPayAmount ?? 0);
      }
    }

    for (const [key, monthData] of monthMap) {
      const amounts = amountMap.get(key);
      if (!amounts || amounts.size < 2) continue;

      const anyMeetsSalaryIndividually = monthData.employers.some(
        (e) => e.meetsSalaryRequirement
      );
      if (anyMeetsSalaryIndividually) continue;

      const totalAmount = Array.from(amounts.values()).reduce(
        (sum, a) => sum + a,
        0
      );
      const threshold = getPremiumPaymentThreshold(
        monthData.year,
        premiumPaymentThreshold
      );
      if (totalAmount >= threshold) {
        for (const employer of monthData.employers) {
          if (!employer.isNonQualifyingPeriod) {
            employer.meetsSalaryRequirementWithAnotherEmployment = true;
          }
        }
      }
    }
  }

  const result = Array.from(monthMap.values()).sort((a, b) => {
    if (a.year !== b.year) return b.year - a.year;
    return b.id.localeCompare(a.id);
  });

  return result;
};

export const getWorksiteAlternatives = (
  worksites?: BisnodeWorksite[]
): GroupedOption[] => {
  if (!worksites) return [];
  const grouped = worksites.reduce(
    (acc, site) => {
      const city =
        site.visitingAddress?.city || site.postalAddress?.city || 'Okänd stad';

      if (!acc[city]) acc[city] = [];
      acc[city].push(site);
      return acc;
    },
    {} as Record<string, typeof worksites>
  );
  return Object.keys(grouped)
    .sort((a, b) => a.localeCompare(b, 'sv'))
    .map((city) => {
      return {
        label: city,
        options: grouped[city].map((site) => {
          const visiting = site.visitingAddress;
          const postal = site.postalAddress;
          const address = visiting?.address || postal?.address;
          const subLabel = address ? `${address}, ${city}` : city;

          return {
            value: site.id,
            label: site.name,
            subLabel: subLabel,
          };
        }),
      };
    });
};

export const ConvertBisnodeWorktoWorksite = (
  bisnodeWorksite?: BisnodeWorksite | null
): Worksite => {
  if (!bisnodeWorksite) {
    return {
      id: '',
      name: '',
      address: '',
      city: '',
      didNotFindWorksiteFromBisnode: false,
    };
  }
  const visiting = bisnodeWorksite.visitingAddress;
  const postal = bisnodeWorksite.postalAddress;
  return {
    id: bisnodeWorksite.id,
    name: bisnodeWorksite.name,
    address: visiting?.address || postal?.address || '',
    city: visiting?.city || postal?.city || '',
    didNotFindWorksiteFromBisnode: false,
  };
};

function getPremiumPaymentThreshold(
  year: number,
  container: PremiumPaymentThresholdContainer
): number {
  if (!Number.isFinite(year)) return container.fallbackValue;
  const match = container.valuesByYear?.[year];
  return match ?? container.fallbackValue;
}
